import React from 'react';
import { _getScreenSize, calculateScreenSize } from '../../utilities/display-utils';
import { Image } from '@msdyn365-commerce/core';
import { generateImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { IBuyboxViewProps, IBuyboxData } from '@msdyn365-commerce-modules/buybox';
import { IBuyboxProps } from '../../definition-extensions/buybox.ext.props.autogenerated';
import { IBuyboxData as IBuyboxViewData } from '../buybox.data';

const renderBanners: React.FC<IBuyboxViewProps & IBuyboxProps<IBuyboxData & IBuyboxViewData>> = props => {
    if (!props) return null;
    const {
        paypalBannerImageDesktop,
        paypalBannerImageMobile,
        paypalBannerImageIpad,
        paypalBannerLink,
        guestUserBannerImageDesktop,
        guestUserBannerImageMobile,
        guestUserBannerImageIpad,
        guestUserBannerLink,
        clubMemberBannerImageDesktop,
        clubMemberBannerImageMobile,
        clubMemberBannerImageIpad,
        clubMemberBannerLink,
        clubPlusMemberBannerImageDesktop,
        clubPlusMemberBannerImageMobile,
        clubPlusMemberBannerImageIpad,
        clubPlusMemberBannerLink
    } = props.config;

    const [screenSize, setScreenSize] = React.useState<number | null>(null);

    React.useEffect(() => {
        const updateSize = () => {
            setScreenSize(calculateScreenSize(window.innerWidth));
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    if (screenSize === null) {
        return null;
    }
    const isMobile = screenSize === 1;
    const isIpad = screenSize === 2;
    const isDesktop = !isMobile && !isIpad;
    const bannerImageAttribute = props.data.productAttributes.result?.find(attribute => attribute.Name?.toLowerCase() === 'bannerimage');
    const showBannerImage = bannerImageAttribute?.TextValue?.toLowerCase() !== 'none';
    const isAuthenticated = props.context.request.user.isAuthenticated;

    const highestActiveLoyaltyCardTier =
        props.data?.loyaltyCard?.result?.LoyaltyGroups?.length &&
        props.data.loyaltyCard.result.LoyaltyGroups[0].HighestActiveLoyaltyCardTier;

    const activeTier = props.data?.loyaltyCard?.result?.LoyaltyGroups?.[0]?.LoyaltyCardTiers?.find(
        (card: any) => card.RecordId === highestActiveLoyaltyCardTier
    );

    const getUserType = (): 'guest' | 'club' | 'clubPlus' => {
        if (!isAuthenticated) return 'guest';
        if (activeTier?.TierId === 'Club Plus') return 'clubPlus';
        if (activeTier?.TierId === 'Club') return 'club';
        return 'guest';
    };

    const userType = getUserType();

    const showPaypal =
        props.data.productPrice?.result?.CustomerContextualPrice &&
        props.data.productPrice?.result?.CustomerContextualPrice > props.context.app.config.payPalBannerThreshold;

    const getImageForScreen = (desktop: any, ipad: any, mobile: any) => {
        if (isMobile) return mobile?.src || '';
        if (isIpad) return ipad?.src || '';
        if (isDesktop) return desktop?.src || '';
        return '';
    };

    const renderPaypal = () => {
        if (!showPaypal) return null;

        const src = getImageForScreen(paypalBannerImageDesktop, paypalBannerImageIpad, paypalBannerImageMobile);

        return (
            <a
                className='msc-cart__static-banner paypal'
                href={paypalBannerLink?.linkUrl?.destinationUrl}
                target={paypalBannerLink?.openInNewTab ? '_blank' : undefined}
                aria-label={paypalBannerLink?.ariaLabel}
                rel='noreferrer'
            >
                <img src={src} alt={paypalBannerLink?.ariaLabel || 'Paypal Banner'} />
            </a>
        );
    };

    const renderMembershipBanner = () => {
        switch (userType) {
            case 'clubPlus': {
                const src = getImageForScreen(
                    clubPlusMemberBannerImageDesktop,
                    clubPlusMemberBannerImageIpad,
                    clubPlusMemberBannerImageMobile
                );
                if (!src) return null;

                return (
                    <a
                        className='msc-cart__static-banner membership club-plus'
                        href={clubPlusMemberBannerLink?.linkUrl?.destinationUrl}
                        target={clubPlusMemberBannerLink?.openInNewTab ? '_blank' : undefined}
                        aria-label={clubPlusMemberBannerLink?.ariaLabel}
                        rel='noreferrer'
                    >
                        <img src={src} alt={clubPlusMemberBannerLink?.ariaLabel || 'Club Plus Member Banner'} />
                    </a>
                );
            }

            case 'club': {
                const src = getImageForScreen(clubMemberBannerImageDesktop, clubMemberBannerImageIpad, clubMemberBannerImageMobile);
                if (!src) return null;

                return (
                    <a
                        className='msc-cart__static-banner membership club'
                        href={clubMemberBannerLink?.linkUrl?.destinationUrl}
                        target={clubMemberBannerLink?.openInNewTab ? '_blank' : undefined}
                        aria-label={clubMemberBannerLink?.ariaLabel}
                        rel='noreferrer'
                    >
                        <img src={src} alt={clubMemberBannerLink?.ariaLabel || 'Club Member Banner'} />
                    </a>
                );
            }

            default: {
                const src = getImageForScreen(guestUserBannerImageDesktop, guestUserBannerImageIpad, guestUserBannerImageMobile);
                if (!src) return null;

                return (
                    <a
                        className='msc-cart__static-banner membership guest'
                        href={guestUserBannerLink?.linkUrl?.destinationUrl}
                        target={guestUserBannerLink?.openInNewTab ? '_blank' : undefined}
                        aria-label={guestUserBannerLink?.ariaLabel}
                        rel='noreferrer'
                    >
                        <img src={src} alt={guestUserBannerLink?.ariaLabel || 'Guest User Banner'} />
                    </a>
                );
            }
        }
    };

    const renderPromoBannerImage = () => {
        let device;
        if (isMobile) {
            device = 'mobile';
        } else if (isIpad) {
            device = 'ipad';
        } else {
            device = 'desktop';
        }
        let bannerImageName = '';
        if (bannerImageAttribute) {
            bannerImageName =
                typeof bannerImageAttribute?.TextValue === 'string'
                    ? bannerImageAttribute.TextValue.replace(/\s+/g, '_').toLowerCase()
                    : '';
            bannerImageAttribute.TextValue = bannerImageName;
        }

        const imagePath = `${device}_${bannerImageName}`;
        let imageUrl = generateImageUrl(imagePath, props.context.request.apiSettings);
        imageUrl = imageUrl?.replace('fileName=/', 'fileName=');

        return (
            <div>
                <Image src={imageUrl} loadFailureBehavior='hide' gridSettings={props.context.request.gridSettings || {}} />
            </div>
        );
    };

    return (
        <div className={`ms-buybox__banners`}>
            {renderPaypal()}
            {renderMembershipBanner()}
            {showBannerImage && renderPromoBannerImage()}
        </div>
    );
};

export default renderBanners;
